import { TableCellComponentProps, CellState } from '@/shared/types/table';
import type { TextColor } from '@/shared/types/text';
import { cn } from '@/shared/utils/cn';
import {
  getTableCellClasses,
  getTableCellProps,
  handleCellClick,
  getContentAlignClass,
  isFlexColumn,
} from '../../utils/cellStyles';
import { Text } from '@/modules/Text';
import { memo } from 'react';

function resolveTableCellTextColor(
  column: TableCellComponentProps['column'],
  state: CellState,
  isRowEditing?: boolean,
): TextColor {
  if (column.textColor) return column.textColor;
  if ((state === 'selected' || state === 'focused') && !isRowEditing) return 'brand-selected';
  return 'primary';
}

export const TableCell = memo(
  ({
    value,
    rawValue,
    row,
    column,
    state = 'default',
    onChange,
    className,
    onCellFocus,
    stickyLeft = 0,
    isLastLocked = false,
    isRowEditing,
    hasColumnMaxWidthAuto = false,
  }: TableCellComponentProps) => {
    const baseClasses = getTableCellClasses(
      state,
      className,
      column.locked,
      isLastLocked,
      undefined,
      isRowEditing,
      false,
      column.width !== undefined || isFlexColumn(column, hasColumnMaxWidthAuto),
    );
    const cellProps = getTableCellProps(
      column,
      stickyLeft,
      undefined,
      false,
      hasColumnMaxWidthAuto,
    );

    const resolvedTextColor = resolveTableCellTextColor(column, state, isRowEditing);

    // Alineamiento del contenido
    const alignClass = getContentAlignClass(column.contentAlign);

    const hasFixedWidth = column.width !== undefined || isFlexColumn(column, hasColumnMaxWidthAuto);
    const innerWrap = cn(
      'ui-flex ui-h-full ui-items-center',
      alignClass,
      hasFixedWidth && 'ui-min-w-0 ui-overflow-hidden',
    );

    if (column.render) {
      // `value` puede ser el nodo ya renderizado en la fila; `render` debe recibir el dato del accessor.
      const renderArg = rawValue !== undefined ? rawValue : value;
      const tooltipFromRaw =
        typeof renderArg === 'string' || typeof renderArg === 'number'
          ? String(renderArg)
          : undefined;
      return (
        <td className={baseClasses} {...cellProps} onClick={(e) => handleCellClick(e, onCellFocus)}>
          <div className={innerWrap}>
            <Text
              type="body"
              size="sm"
              weight="regular"
              color={resolvedTextColor}
              as="div"
              truncateLabel={hasFixedWidth}
              tooltipTitle={tooltipFromRaw}
              className={cn(hasFixedWidth && 'ui-w-full ui-min-w-0')}
            >
              {column.render(renderArg, row, column) as React.ReactNode}
            </Text>
          </div>
        </td>
      );
    }

    const displayValue = value ? String(value) : '-';
    return (
      <td className={baseClasses} {...cellProps} onClick={(e) => handleCellClick(e, onCellFocus)}>
        <div className={innerWrap}>
          <Text
            type="body"
            size="sm"
            weight="regular"
            color={resolvedTextColor}
            truncateLabel={hasFixedWidth}
            tooltipTitle={displayValue}
            className={cn(hasFixedWidth && 'ui-block ui-min-w-0 ui-max-w-full')}
          >
            {displayValue}
          </Text>
        </div>
      </td>
    );
  },
);

TableCell.displayName = 'TableCell';
